/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Attendance logic module.
 * Handles event attendance tracking, CSV generation, QR code scanning,
 * and attendee status management for both regular and business events.
 * @module logic/attendance
 */

import { refreshToken, signOut } from "../../utils/common.js";
import dayjs from "dayjs";
import { getActiveEvents as getActiveRegistrationEvents } from "../events";
import { getActiveBusinessEvents } from "../businessEvents";
import { getActiveB2BEvents } from "../b2bEvents";
import {
  getActiveBreakfastEvents,
  updateBreakfastEventAttendance,
} from "../breakfastEvents";
import { getUser } from "../user";
import {
  canViewPastEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import i18n from "../../i18n.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import { getCsvHeadersForEventType } from "./csvHeaders.js";
import { formatAttendees } from "./formatAttendees.js";

export const APPOINTMENT_REJECTED_MESSAGE = "Appointment is rejected";

// API Functions

/**
 * Fetches and sets user role/permission data.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} changePermissions - Callback to update permissions state
 * @returns {Promise<object|undefined>} Role permissions when available
 */
export const getUserRoleData = async (navigate, changePermissions) => {
  const tokenToUse = localStorage.getItem("token");
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${tokenToUse}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${localStorage.getItem(
        "userId",
      )}`,
      options,
    );
    const roleData = await response.json();
    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return undefined;
      }
      return await getUserRoleData(navigate, changePermissions);
    }
    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
      return roleData.permisos;
    }
    return undefined;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return undefined;
  }
};

/**
 * Fetches and combines active events for the Attendance dropdown.
 * Eligible users also receive past non-cancelled events (futures first).
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setEventsList - State setter for events list
 * @param {object|null|undefined} [permissions]
 * @returns {Promise<void>}
 */
export const getActiveData = async (navigate, setEventsList, permissions) => {
  const userId = localStorage.getItem("userId");
  const user = userId ? await getUser(userId, navigate) : null;
  const includePast = canViewPastEvents(user, permissions);

  const events = await getActiveRegistrationEvents(navigate, {
    includePast,
    userId,
  });
  const businessEvents = await getActiveBusinessEvents(navigate);
  const b2bEvents = await getActiveB2BEvents(navigate, { includePast });
  const breakfastEvents = await getActiveBreakfastEvents(navigate);

  const activeEvents = sortEventsFuturesFirst([
    ...(events || []),
    ...(businessEvents || []),
    ...(b2bEvents || []),
    ...(breakfastEvents || []),
  ]);
  setEventsList(activeEvents);
};

/**
 * Fetches attendees for a regular event.
 * @async
 * @param {Object} event - Event object with _id
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setAttendanceData - State setter for attendance data
 * @returns {Promise<void>}
 */
export const getEventAttendees = async (event, navigate, setAttendanceData) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/event/${event._id}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getEventAttendees(event, navigate, setAttendanceData);
      return;
    }
    setAttendanceData(response.ok && Array.isArray(data) ? data : []);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    setAttendanceData([]);
  }
};

/**
 * Fetches attendees for a business event.
 * @async
 * @param {Object} event - Business event object with _id
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setAttendanceData - State setter for attendance data
 * @returns {Promise<void>}
 */
export const getBusinessEventAttendees = async (
  event,
  navigate,
  setAttendanceData,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/businessEvent/${event._id}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getBusinessEventAttendees(event, navigate, setAttendanceData);
      return;
    }
    setAttendanceData(response.ok && Array.isArray(data) ? data : []);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    setAttendanceData([]);
  }
};

/**
 * Fetches attendees for a B2B event.
 * @async
 * @param {Object} event - B2B event object with _id
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setAttendanceData - State setter for attendance data
 * @returns {Promise<void>}
 */
export const getB2BEventAttendees = async (
  event,
  navigate,
  setAttendanceData,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent/${event._id}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getB2BEventAttendees(event, navigate, setAttendanceData);
      return;
    }
    setAttendanceData(response.ok && Array.isArray(data) ? data : []);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    setAttendanceData([]);
  }
};

/**
 * Confirms B2B event attendance (marks user attended or not).
 * @async
 * @param {string} idUsuario - User identifier
 * @param {string} idEventoB2B - B2B event identifier
 * @param {string} idCita - Appointment identifier
 * @param {boolean} estatus - Attendance status
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>}
 */
export const confirmB2BAttendance = async (
  idUsuario,
  idEventoB2B,
  idCita,
  estatus,
  navigate,
) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      idUsuario,
      idEventoB2B,
      idCita,
      estatus,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent/attendance/confirm`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return confirmB2BAttendance(
        idUsuario,
        idEventoB2B,
        idCita,
        estatus,
        navigate,
      );
    }

    if (!response.ok) {
      throw new Error(
        apiMessageOrGeneric(
          data?.message,
          "FAILED_TO_CONFIRM_B2B_ATTENDANCE",
        ),
      );
    }

    return data;
  } catch (error) {
    logger.error("Error confirming B2B attendance:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};

/**
 * Registers attendance for a business event appointment.
 * @async
 * @param {string} appointmentId - Appointment identifier
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<null>}
 */
export const handleBusinessEventRegister = async (
  appointmentId,
  eventId,
  navigate,
) => {
  const utcDate = dayjs().utc("z").toISOString();

  const attendanceData = {
    idUsuario: localStorage.getItem("userId"),
    idCita: appointmentId,
    idEvento: eventId,
    fechaInscripcion: utcDate,
  };

  try {
    const options = {
      method: "POST",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(attendanceData),
    };
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/businessEvent`,
      options,
    );

    if (response.ok) {
      await response.json();
      return null;
    }

    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return handleBusinessEventRegister(appointmentId, eventId, navigate);
    }

    logger.error("Failed to register in event:", {
      data: [response.status, errorData.message],
    });
    return null;
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Updates an attendee's attendance status.
 * @async
 * @param {string} userId - User identifier
 * @param {string} eventId - Event identifier
 * @param {boolean} status - New attendance status
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Updated attendance data
 * @throws {Error} If update fails
 */
export const updateAttendance = async (userId, eventId, status, navigate) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      idUsuario: userId,
      idEvento: eventId,
      estatus: status,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/attendance/status`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return updateAttendance(userId, eventId, status, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error confirming attendance:", { err: sanitizeError(error) });
    throw error;
  }
};

export { formatAttendees } from "./formatAttendees.js";

/**
 * Generates CSV content from attendee data.
 * @param {Array} attendees - Array of attendee objects
 * @param {Object} selectedEvent - Selected event with nombre property
 * @returns {string} CSV content string
 */
export const generateCsvData = (attendees, selectedEvent) => {
  const formattedAttendees = formatAttendees(attendees, selectedEvent);
  const csvHeaders = getCsvHeadersForEventType(selectedEvent.tipoEvento);

  let csvContent = "";
  const csvTitle = i18n.t("exports.attendanceCsv.title", {
    eventName: selectedEvent.nombre,
  });
  const csvSubtitle = i18n.t("exports.attendanceCsv.downloaded", {
    date: dayjs().locale(i18n.language).utc().format("LL"),
  });
  csvContent += `${csvTitle}\n`;
  csvContent += `${csvSubtitle}\n`;

  csvContent += csvHeaders.join(",") + "\n";

  formattedAttendees.forEach((attendee) => {
    csvContent += Object.values(attendee).join(",") + "\n";
  });
  return csvContent;
};

/**
 * Downloads CSV data as a file.
 * @param {string} data - CSV content string
 * @param {Object} selectedEvent - Selected event for filename
 * @returns {void}
 */
export const downloadCsv = (data, selectedEvent) => {
  const currentDate = dayjs().utc().format("DDMMYY");
  const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${selectedEvent.nombre}_${currentDate}.csv`;
  a.click();
};

/**
 * Handles CSV download for attendance data.
 * @param {Array} attendanceData - Array of attendance records
 * @param {Object} selectedEvent - Selected event object
 * @returns {void}
 */
export const handleDownloadCsv = (attendanceData, selectedEvent) => {
  if (attendanceData.length === 0) {
    logger.info("Error - No data to download");
    return;
  }
  const csvData = generateCsvData(attendanceData, selectedEvent);
  downloadCsv(csvData, selectedEvent);
};

/**
 * Handles toggling attendance status with confirmation modal for existing attendance.
 * @async
 * @param {number} attendeeIndex - Index of attendee in data array
 * @param {Array} attendanceData - Array of attendance records
 * @param {Function} setCurrentAttendeeIndex - State setter for current index
 * @param {Function} setIsConfirmationModalOpen - State setter for modal visibility
 * @param {Function} updateAttendanceStatusFn - Function to update status
 * @returns {Promise<void>}
 */
export const handleToggleActive = async (
  attendeeIndex,
  attendanceData,
  setCurrentAttendeeIndex,
  setIsConfirmationModalOpen,
  updateAttendanceStatusFn,
) => {
  setCurrentAttendeeIndex(attendeeIndex);
  if (attendanceData[attendeeIndex].asistio === true) {
    setIsConfirmationModalOpen(true);
  } else {
    await updateAttendanceStatusFn(attendeeIndex);
  }
};

/**
 * Updates attendance status for an attendee (handles both regular and business events).
 * @async
 * @param {Object} params
 * @param {number} params.attendeeIndex - Index of attendee in data array
 * @param {number|null} params.currentAttendeeIndex - Current attendee index from state
 * @param {Array} params.attendanceData - Array of attendance records
 * @param {Function} params.setAttendanceData - State setter for attendance data
 * @param {Function} params.setCurrentAttendeeIndex - State setter for current index
 * @param {Function} params.setIsConfirmationModalOpen - State setter for modal visibility
 * @param {Function} params.updateRegularAttendance - Function to update regular attendance
 * @param {Function} params.navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const updateAttendaceStatus = async ({
  attendeeIndex,
  currentAttendeeIndex,
  attendanceData,
  setAttendanceData,
  setCurrentAttendeeIndex,
  setIsConfirmationModalOpen,
  updateRegularAttendance,
  navigate,
}) => {
  const currAttendeeIndex = currentAttendeeIndex ?? attendeeIndex;
  if (currAttendeeIndex === null || currAttendeeIndex === undefined) {
    logger.info("No attendee selected");
    return;
  }

  const attendee = attendanceData[currAttendeeIndex];
  if (!attendee) {
    logger.info("No attendee found at index");
    return;
  }

  const userId = attendee.idUsuario;
  let eventId = attendee.idEvento;
  const buyerCompanyId = attendee?.idEmpresaCompradora;
  const appointmentId = attendee?.idCita;
  const newStatus = !attendee.asistio;

  // Only update the specific item instead of mapping entire array
  const updatedAttendanceData = [...attendanceData];
  updatedAttendanceData[currAttendeeIndex] = {
    ...attendee,
    asistio: newStatus,
  };

  if (attendee.idEventoB2B && appointmentId) {
    await confirmB2BAttendance(
      userId,
      attendee.idEventoB2B,
      appointmentId,
      newStatus,
      navigate,
    );
  } else if (buyerCompanyId && appointmentId) {
    eventId = attendee.idEncuentroNegocios;
    await confirmBusinessEventAttendance(
      userId,
      eventId,
      buyerCompanyId,
      appointmentId,
      newStatus,
      navigate,
    );
  } else if (attendee.idEventoDesayuno && userId) {
    await updateBreakfastEventAttendance(
      userId,
      attendee.idEventoDesayuno,
      newStatus,
      navigate,
    );
  } else if (eventId && userId) {
    await updateRegularAttendance(userId, eventId, newStatus, navigate);
  } else {
    logger.warn("No matching attendance update condition for attendee", {
      data: { attendeeId: attendee?.id, userId, eventId },
    });
  }
  setCurrentAttendeeIndex(null);
  setIsConfirmationModalOpen(false);
  setAttendanceData(updatedAttendanceData);
};

/**
 * Confirms attendance for a business event appointment.
 * @async
 * @param {string} userId - User identifier
 * @param {string} eventId - Business event identifier
 * @param {string} buyerCompanyId - Buyer company identifier
 * @param {string} appointmentId - Appointment identifier
 * @param {boolean} newStatus - New attendance status
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Updated attendance data
 * @throws {Error} If confirmation fails
 */
export const confirmBusinessEventAttendance = async (
  userId,
  eventId,
  buyerCompanyId,
  appointmentId,
  newStatus,
  navigate,
) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      idUsuario: userId,
      idEvento: eventId,
      idEmpresaCompradora: buyerCompanyId,
      idCita: appointmentId,
      estatus: newStatus,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/businessEvent/attendance/confirm`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return confirmBusinessEventAttendance(
        userId,
        eventId,
        buyerCompanyId,
        appointmentId,
        newStatus,
        navigate,
      );
    }

    if (!response.ok) {
      throw new Error(
        apiMessageOrGeneric(
          data?.message,
          "FAILED_TO_CONFIRM_BUSINESS_EVENT_ATTENDANCE",
        ),
      );
    }

    return data;
  } catch (error) {
    logger.error("Error confirming attendance:", { err: sanitizeError(error) });
    throw error;
  }
};

/**
 * Updates attendance data after QR code scan.
 * @param {string} userId - User identifier
 * @param {string} eventId - Event identifier
 * @param {Array} attendanceData - Current attendance data array
 * @param {Function} setAttendanceData - State setter for attendance data
 * @returns {void}
 */
export const handleQRCodeAttendanceUpdate = (
  userId,
  eventId,
  attendanceData,
  setAttendanceData,
) => {
  const updatedAttendanceData = attendanceData.map((attendee) => {
    if (attendee.idUsuario === userId && attendee.idEvento === eventId) {
      return {
        ...attendee,
        asistio: true,
      };
    }
    return attendee;
  });
  setAttendanceData(updatedAttendanceData);
};

/**
 * Handles successful QR code scan by refreshing attendance data.
 * @async
 * @param {string} status - Scan status ('success' or other)
 * @param {string} userId - User identifier
 * @param {string} eventId - Event identifier
 * @param {Object} selectedEvent - Currently selected event
 * @param {Function} getEventAttendeesWrapper - Function to fetch attendees
 * @returns {Promise<void>}
 */
export const handleQRCodeScanned = async (
  status,
  userId,
  eventId,
  selectedEvent,
  getEventAttendeesWrapper,
) => {
  logger.info("handleQRCodeScanned called with:", {
    data: {
      status,
      userId,
      eventId,
    },
  });

  if (status === "success" && selectedEvent) {
    logger.info("QR code scan successful, refetching attendance data");
    await getEventAttendeesWrapper(selectedEvent);
  }
};

/**
 * Handles change in results per page limit.
 * @param {Object} selectedOption - Selected option with value property
 * @param {Function} setResultsLimit - State setter for results limit
 * @param {Function} setCurrentPage - State setter for current page
 * @returns {void}
 */
export const handleResultsLimitChange = (
  selectedOption,
  setResultsLimit,
  setCurrentPage,
) => {
  setResultsLimit(selectedOption.value);
  setCurrentPage(1);
};

/**
 * Handles pagination page changes.
 * @param {string} direction - Direction ('next' or 'prev')
 * @param {number} currentPage - Current page number
 * @param {Array} attendanceData - Attendance data array
 * @param {number} resultsLimit - Results per page
 * @param {Function} setCurrentPage - State setter for current page
 * @returns {void}
 */
export const handlePageChange = (
  direction,
  currentPage,
  attendanceData,
  resultsLimit,
  setCurrentPage,
) => {
  if (
    direction === "next" &&
    currentPage < Math.ceil(attendanceData.length / resultsLimit)
  ) {
    setCurrentPage((prevPage) => prevPage + 1);
  } else if (direction === "prev" && currentPage > 1) {
    setCurrentPage((prevPage) => prevPage - 1);
  } else {
    // Unsupported direction or already at bounds. No action required.
  }
};

/**
 * Toggles sort direction for attendance table.
 * @param {Object} sortConfig - Current sort configuration
 * @param {Function} setSortConfig - State setter for sort config
 * @returns {void}
 */
export const handleSort = (sortConfig, setSortConfig) => {
  const newDirection =
    sortConfig.direction === "ascending" ? "descending" : "ascending";
  setSortConfig({ key: "nombre", direction: newDirection });
};

/**
 * Handles search input changes.
 * @param {Event} e - Input change event
 * @param {Function} setSearchQuery - State setter for search query
 * @param {Function} setCurrentPage - State setter for current page
 * @returns {void}
 */
export const handleSearch = (e, setSearchQuery, setCurrentPage) => {
  setSearchQuery(e.target.value);
  setCurrentPage(1);
};

/**
 * Filters attendance data by search query.
 * @param {Array} attendanceData - Array of attendance records
 * @param {string} searchQuery - Search string
 * @param {boolean} isBusinessEvent - Whether filtering business event attendees
 * @returns {Array} Filtered attendance records
 */
export const filterAttendances = (
  attendanceData,
  searchQuery,
  isBusinessEvent,
  isB2BEvent,
  isBreakfastEvent,
) => {
  const useBusinessFields = isBusinessEvent || isB2BEvent || isBreakfastEvent;
  if (useBusinessFields) {
    return attendanceData.filter((attendee) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        attendee.nombreUsuario?.toLowerCase().includes(searchLower) ||
        attendee.correoElectronicoUsuario
          ?.toLowerCase()
          .includes(searchLower) ||
        attendee.rfcEmpresa?.toLowerCase().includes(searchLower) ||
        attendee.nombreEmpresa?.toLowerCase().includes(searchLower)
      );
    });
  } else {
    return attendanceData.filter((attendee) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        attendee.nombre?.toLowerCase().includes(searchLower) ||
        attendee.correoElectronico?.toLowerCase().includes(searchLower) ||
        attendee.RFC?.toLowerCase().includes(searchLower)
      );
    });
  }
};

/**
 * Paginates filtered attendance data.
 * @param {Array} filteredAttendances - Filtered attendance records
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} resultsLimit - Number of results per page
 * @returns {Array} Paginated slice of attendance records
 */
export const paginateAttendances = (
  filteredAttendances,
  currentPage,
  resultsLimit,
) => {
  return filteredAttendances.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );
};
